import { faCircle, faTrashCan, faUserEdit } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { useEffect, useMemo, useState } from "react";
import { Dropdown, DropdownButton, InputGroup } from "react-bootstrap";
import { CSVLink } from "react-csv";
import { useTranslation } from "react-i18next";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import { useSearchParams } from "react-router";
import Pagination from "../../components/Pagination/Pagination";
import {
  useActivateUserMutation,
  useDeactivateUserMutation,
  useDeleteUserMutation,
  useGetUserByIdQuery,
  useGetUsersQuery,
  useUpdateUserMutation,
} from "../../services/API/UsersAPI";
import { ROLES, isAllowedToDeleteUsers, isAllowedToDownloadUsers } from "../../services/auth/Roles";
import { dynamicSort } from "../../services/Sort/dynamicSort";
import countries from "../../utils/i18n-iso-countries-registered";
import DeleteConfirmation from "./DeleteConfirmation";
import exportUsersAsCsv from "./Export/exportUsersAsCsv";
import UserEditModal from "./UserEditModal";
import "./UsersList.scss";
import { useSelector } from "react-redux";
import { selectCurrentRoles } from "../../store/slices/auth/authSlice";

const UsersList = () => {
  const { i18n, t } = useTranslation(["translation"]);

  const [searchPhrase, setSearchPhrase] = useState("");
  const handleSearch = (e) => {
    setSearchPhrase(e.target.value);
  };

  const { data: users } = useGetUsersQuery({});
  const [updateUser] = useUpdateUserMutation();
  const [deleteUser] = useDeleteUserMutation();
  const [activateUser] = useActivateUserMutation();
  const [deactivateUser] = useDeactivateUserMutation();

  const [formState, setFormState] = useState({});

  let PageSize = 12;
  const [currentPage, setCurrentPage] = useState(1);
  const [sortedBy, setSortedBy] = useState("");

  function setSortBy(params) {
    if (params === sortedBy) {
      let sort = "-" + params;
      setSortedBy(sort);
      return;
    }
    setSortedBy(params);
  }

  const [fullTableData, setFullTableData] = useState([]);

  const currentTableData = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * PageSize;
    const lastPageIndex = firstPageIndex + PageSize;
    return fullTableData?.slice(firstPageIndex, lastPageIndex);
  }, [currentPage, PageSize, fullTableData]);

  //filter
  useEffect(() => {
    // Mapping Numbers to Account Status
    var result = users?.map(function (item) {
      const { active, hasConfirmedEmail } = item;

      let status = active ? 0 : 2;
      if (!hasConfirmedEmail) {
        status = 1;
      }
      return { ...item, status: status };
    });
    let langKey;
    if (i18n.language === "de-DE") {
      langKey = "DE";
    } else if (i18n.language === "es-ES") {
      langKey = "ES";
    } else {
      langKey = "EN";
    }

    let modifiedArray = result?.filter((item) => {
      let translatedCounty = countries.getName(item.country, langKey, {
        select: "official",
      });
      return (
        item.name.toLowerCase().includes(searchPhrase.toLowerCase()) ||
        item.email.toLowerCase().includes(searchPhrase.toLowerCase()) ||
        item.surname.toLowerCase().includes(searchPhrase.toLowerCase()) ||
        item.company_name.toLowerCase().includes(searchPhrase.toLowerCase()) ||
        translatedCounty.toLowerCase().includes(searchPhrase.toLowerCase())
      );
    });

    // sort
    if (sortedBy !== "") {
      modifiedArray?.sort(dynamicSort(sortedBy));
    }
    setFullTableData(modifiedArray);
    setCurrentPage(1);
  }, [i18n.language, searchPhrase, sortedBy, users]);

  const [showDelete, setShowDelete] = useState(false);
  const handleDeleteShow = (user) => {
    setFormState({ id: user._id, ...user });
    setShowDelete(true);
  };

  const handleDeleteClose = () => setShowDelete(false);
  const handleDelete = () => {
    deleteUser({ id: formState.id });
    setShowDelete(false);
  };

  const [showEdit, setShowEdit] = useState(false);
  const handleEditShow = (user) => {
    setFormState({ id: user._id, ...user });
    setShowEdit(true);
  };
  const handleEditClose = () => {
    clearUserIdQueryParam();
    setShowEdit(false);
  };

  const handleEdit = () => {
    let { active, ...rest } = formState;

    // Order is important incase of fixing the email
    updateUser({ id: formState.id, user: rest });
    activateUser({ id: formState.id, active: active });

    clearUserIdQueryParam();
    setShowEdit(false);
  };

  const handleChange = ({ target: { name, value } }) => {
    return setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleActivateUserChange = ({ target: { name, checked } }) => {
    return setFormState((prev) => ({ ...prev, [name]: checked }));
  };

  const handleRoleChangeCheckbox = ({ target: { name, checked } }) => {
    // TODO: improve this !
    let value;
    if (checked) {
      if (name === "Superadmin") {
        value = ROLES.Superadmin;
      }
      if (name === "Admin") {
        value = ROLES.Admin;
      }
      if (name === "Approver") {
        value = ROLES.Approver;
      }
      if (name === "User") {
        value = ROLES.User;
      }
    }
    return setFormState((prev) => ({
      ...prev,
      roles: { ...prev.roles, [name]: value },
    }));
  };

  const [skip, setSkip] = useState(true);
  const [queryParameter, setQueryParameter] = useSearchParams();
  const uid = queryParameter.get("uid");
  const { data: user, isSuccess } = useGetUserByIdQuery(uid, {
    skip,
  });

  function clearUserIdQueryParam() {
    queryParameter.delete("uid");
    setQueryParameter(queryParameter);
  }

  useEffect(() => {
    if (uid) {
      setSkip(false);
      if (isSuccess && user) {
        setFormState({ id: user._id, ...user });
        setShowEdit(true);
      }
    }
  }, [uid, user, isSuccess]);

  // Have to register the languages you want to use
  let langKey;
  if (i18n.language === "de-DE") {
    langKey = "DE";
  } else if (i18n.language === "es-ES") {
    langKey = "ES";
  } else {
    langKey = "EN";
  }

  function SortIcon(value) {
    if (sortedBy === value) return <FaSortUp />;
    if (sortedBy === "-" + value) return <FaSortDown />;
    return <FaSort />;
  }

  const [checkedState, setCheckedState] = useState(new Array(PageSize).fill(false));

  const [allCheckboxesChecked, setAllCheckboxesChecked] = useState(false);

  const setAllCheckboxes = (value) => {
    setAllCheckboxesChecked(value);
    setCheckedState(new Array(PageSize).fill(value));
  };

  const handleOnChange = (position) => {
    const updatedCheckedState = checkedState.map((item, index) =>
      index === position ? !item : item
    );
    setCheckedState(updatedCheckedState);
  };

  const [showDeleteMultiple, setShowDeleteMultiple] = useState(false);
  const handleDeleteMultipleClose = () => setShowDeleteMultiple(false);
  function handleDeleteMultiple() {
    currentTableData.forEach(function (item, index) {
      if (checkedState[index]) {
        deleteUser({ id: item._id });
      }
    });

    setAllCheckboxes(false);
    setShowDeleteMultiple(false);
  }

  const roles = useSelector(selectCurrentRoles);

  return (
    <div className="Users">
      <div className="container-fluid">
        <div className="table-wrapper">
          <div className="table-title">
            <div className="row">
              <div className="col-sm-6">
                <h2>{t("userList.title")}</h2>
              </div>

              <div className="col-sm-6">
                <label className="float-end" htmlFor="search">
                  <span className="me-2">{t("userList.search")}</span>
                  <input id="search" type="text" onChange={handleSearch} />
                </label>
                {isAllowedToDownloadUsers(roles) && (
                  <CSVLink
                    className="btn btn-success me-3"
                    data={exportUsersAsCsv.export(users)}
                    headers={exportUsersAsCsv.headers()}
                    filename={"UserExport.csv"}
                  >
                    {t("userList.export.downloadAll")}
                  </CSVLink>
                )}
                {/* 
                  <a
                    href="#addUserModal"
                    className="btn btn-success"
                    data-toggle="modal"
                  >
                    <FontAwesomeIcon
                      data-toggle="tooltip"
                      size="lg"
                      title="New"
                      color="white"
                      icon={faPlusCircle}
                      className="me-2"
                    />
                    <span>Add New User</span>
                  </a>
                  <a
                    href="#deleteUserModal"
                    className="btn btn-danger"
                    data-toggle="modal"
                  >
                    <FontAwesomeIcon
                      data-toggle="tooltip"
                      size="lg"
                      title="Delete"
                      color="white"
                      icon={faPlusCircle}
                      className="me-2"
                    />
                    <span>Delete</span>
                  </a>
                */}
              </div>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table table-striped table-hover">
              <thead>
                <tr>
                  <th>
                    <span className="custom-checkbox">
                      <input
                        type="checkbox"
                        id="selectAll"
                        name="selectAll"
                        checked={allCheckboxesChecked}
                        onChange={(e) => {
                          e.target.checked ? setAllCheckboxes(true) : setAllCheckboxes(false);
                        }}
                      />
                      <label htmlFor="selectAll"></label>
                    </span>
                  </th>
                  <th>
                    <span className="searchHeader" onClick={() => setSortBy("username")}>
                      {t("userList.tableHeader.username")}
                      {SortIcon("username")}
                    </span>
                  </th>
                  <th>
                    <span className="searchHeader" onClick={() => setSortBy("email")}>
                      {t("userList.tableHeader.email")}
                      {SortIcon("email")}
                    </span>
                  </th>
                  <th>
                    <span className="searchHeader" onClick={() => setSortBy("surname")}>
                      {t("userList.tableHeader.fullName")}
                      {SortIcon("surname")}
                    </span>
                  </th>
                  <th>
                    <span className="searchHeader" onClick={() => setSortBy("company_name")}>
                      {t("userList.tableHeader.company")}
                      {SortIcon("company_name")}
                    </span>
                  </th>
                  <th>
                    <span className="searchHeader" onClick={() => setSortBy("country")}>
                      {t("userList.tableHeader.country")}
                      {SortIcon("country")}
                    </span>
                  </th>
                  <th>
                    <div className="searchHeader" onClick={() => setSortBy("status")}>
                      {t("userList.tableHeader.status")}
                      {SortIcon("status")}
                    </div>
                  </th>
                  <th>
                    <div className="searchHeader" onClick={() => setSortBy("createdAt")}>
                      {t("userList.editModal.registerdAt")}
                      {SortIcon("createdAt")}
                    </div>
                  </th>
                  <th>
                    <div className="searchHeader" onClick={() => setSortBy("lastActiveAt")}>
                      {t("userList.editModal.lastActive")}
                      {SortIcon("lastActiveAt")}
                    </div>
                  </th>

                  <th>{t("userList.tableHeader.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {currentTableData?.map((user, index) => {
                  let activeAt;
                  if (user?.lastActiveAt !== undefined) {
                    activeAt = new Date(user?.lastActiveAt);
                  }

                  let registeredAt;
                  if (user?.register_date !== undefined) {
                    registeredAt = new Date(user?.createdAt);
                  }

                  return (
                    <tr key={index}>
                      <td>
                        <span className="custom-checkbox">
                          <input
                            type="checkbox"
                            id={`checkbox_${user.username}`}
                            data-testid={`user-checkbox-${user.username}`}
                            name={`checkbox_${user.username}`}
                            value={user._id}
                            checked={checkedState[index]}
                            onChange={() => handleOnChange(index)}
                          />
                          <label htmlFor={`checkbox_${user.username}`}></label>
                        </span>
                      </td>
                      <td>{user?.username}</td>
                      <td>{user?.email}</td>
                      <td>
                        {user?.surname}, {user?.name}
                      </td>
                      <td>{user?.company_name}</td>
                      <td>{countries.getName(user?.country, langKey)}</td>
                      <td>
                        <span
                          className={classNames({
                            status: true,
                            "text-success": user.status === 0,
                            "text-warning": user.status === 2,
                            "text-danger": user.status === 1,
                          })}
                        >
                          <FontAwesomeIcon
                            data-toggle="tooltip"
                            title="Status"
                            icon={faCircle}
                            size="2xs"
                            className="mx-2"
                          />
                        </span>
                      </td>
                      <td>{registeredAt?.toLocaleDateString() ?? ""}</td>
                      <td>{activeAt?.toLocaleDateString() ?? ""}</td>
                      <td>
                        <span
                          data-testid="user-edit"
                          data-toggle="modal"
                          onClick={() => handleEditShow(user)}
                        >
                          <FontAwesomeIcon
                            id="clickableAwesomeFont"
                            data-toggle="tooltip"
                            title="Edit"
                            icon={faUserEdit}
                            className="mx-2"
                          />
                        </span>
                        {isAllowedToDeleteUsers(roles) && (
                          <span
                            data-testid="user-delete"
                            data-toggle="modal"
                            onClick={() => handleDeleteShow(user)}
                          >
                            <FontAwesomeIcon
                              id="clickableAwesomeFont"
                              data-toggle="tooltip"
                              title="Delete"
                              color="red"
                              className="mx-2"
                              icon={faTrashCan}
                            />
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <InputGroup className="mb-3">
            <DropdownButton
              variant="outline-secondary"
              title={t("userList.actions")}
              id="input-group-dropdown-1"
            >
              <Dropdown.Item
                onClick={(e) => {
                  currentTableData.forEach(function (item, index) {
                    if (checkedState[index]) {
                      activateUser({ id: item._id, active: true });
                    }
                  });
                  setAllCheckboxes(false);
                }}
              >
                {t("userList.activate")}
              </Dropdown.Item>

              <Dropdown.Item
                onClick={(e) => {
                  currentTableData.forEach(function (item, index) {
                    if (checkedState[index]) {
                      deactivateUser({ id: item._id });
                    }
                  });
                  setAllCheckboxes(false);
                }}
              >
                {t("userList.deactivate")}
              </Dropdown.Item>
              {isAllowedToDeleteUsers(roles) && (
                <Dropdown.Item
                  onClick={(e) => {
                    setShowDeleteMultiple(true);
                  }}
                >
                  {t("userList.delete")}
                </Dropdown.Item>
              )}
            </DropdownButton>
          </InputGroup>

          {currentTableData && (
            <Pagination
              className="pagination-bar"
              currentPage={currentPage}
              totalCount={fullTableData?.length}
              pageSize={PageSize}
              onPageChange={(page) => setCurrentPage(page)}
            />
          )}
        </div>
      </div>

      <div id="editUserModal">
        <UserEditModal
          formState={formState}
          handleActivateUserChange={handleActivateUserChange}
          handleChange={handleChange}
          handleEdit={handleEdit}
          handleEditClose={handleEditClose}
          handleRoleChangeCheckbox={handleRoleChangeCheckbox}
          show={showEdit}
        />
      </div>

      {/* Single */}
      <DeleteConfirmation
        title={t("userList.deleteModal.title")}
        handleCancel={handleDeleteClose}
        handleSubmit={handleDelete}
        show={showDelete}
      />

      {/* Multiple */}
      <DeleteConfirmation
        title={t("userList.deleteModal.title")}
        handleCancel={handleDeleteMultipleClose}
        handleSubmit={handleDeleteMultiple}
        show={showDeleteMultiple}
      />
    </div>
  );
};
export default UsersList;
